<template>
  <main class="adm-page">

    <!-- ── Header ──────────────────────────────────── -->
    <div class="adm-header">
      <div>
        <h1 class="adm-header__title">Gestion des Contacts</h1>
        <p class="adm-header__sub">{{ contactsPagination.total }} messages au total</p>
      </div>
      <div class="adm-header__actions">
        <div style="position:relative">
          <button class="adm-btn adm-btn--outline" @click="toggleExportMenu"><Download />Exporter</button>
          <div v-if="showExportMenu" class="adm-drop__menu" style="top:calc(100%+.35rem);right:0">
            <button class="adm-drop__item" @click="exportToExcel"><FileSpreadsheet />Export Excel</button>
            <button class="adm-drop__item" @click="exportToPDF"><FileText />Export PDF</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Loader ────────────────────────────────────── -->
    <div v-if="loading" class="adm-loading"><span class="adm-ring"></span></div>

    <template v-else>
      <!-- ── KPIs ─────────────────────────────────────── -->
      <div class="adm-kpis">
        <div class="adm-kpi adm-kpi--forest">
          <div class="adm-kpi__icon"><Inbox /></div>
          <span class="adm-kpi__val">{{ contactStats.total || 0 }}</span>
          <span class="adm-kpi__lbl">Total messages</span>
        </div>
        <div class="adm-kpi adm-kpi--amber">
          <div class="adm-kpi__icon"><Mail /></div>
          <span class="adm-kpi__val">{{ contactStats.non_lus || 0 }}</span>
          <span class="adm-kpi__lbl">Non lus</span>
        </div>
        <div class="adm-kpi adm-kpi--sage">
          <div class="adm-kpi__icon"><Clock /></div>
          <span class="adm-kpi__val">{{ contactStats.en_cours || 0 }}</span>
          <span class="adm-kpi__lbl">En cours</span>
        </div>
        <div class="adm-kpi adm-kpi--terra">
          <div class="adm-kpi__icon"><Flag /></div>
          <span class="adm-kpi__val">{{ contactStats.urgents || 0 }}</span>
          <span class="adm-kpi__lbl">Urgents</span>
        </div>
      </div>

      <!-- ── Filtres ──────────────────────────────────── -->
      <div class="adm-filters">
        <div class="adm-search" style="flex:1;min-width:200px">
          <Search />
          <input v-model="filters.search" @input="debouncedSearch" placeholder="Rechercher…" class="adm-input" />
        </div>
        <select v-model="filters.status" @change="applyFilters" class="adm-input adm-input--sm">
          <option value="">Tous statuts</option>
          <option value="nouveau">Nouveau</option>
          <option value="en_cours">En cours</option>
          <option value="traité">Traité</option>
          <option value="archivé">Archivé</option>
        </select>
        <select v-model="filters.priority" @change="applyFilters" class="adm-input adm-input--sm">
          <option value="">Toutes priorités</option>
          <option value="basse">Basse</option>
          <option value="normale">Normale</option>
          <option value="haute">Haute</option>
          <option value="urgente">Urgente</option>
        </select>
        <button class="adm-btn adm-btn--outline adm-btn--sm" @click="resetFilters"><X />Réinitialiser</button>
      </div>

      <!-- ── Table ────────────────────────────────────── -->
      <div class="adm-card">
        <div class="adm-table-wrap">
          <table class="adm-table">
            <thead>
              <tr>
                <th><button @click="sortBy('first_name')">Contact</button></th>
                <th>Coordonnées</th>
                <th>Sujet</th>
                <th><button @click="sortBy('priority')">Priorité</button></th>
                <th><button @click="sortBy('status')">Statut</button></th>
                <th><button @click="sortBy('created_at')">Date</button></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!contacts.length">
                <td colspan="7" class="adm-table__empty">Aucun contact trouvé</td>
              </tr>
              <tr v-for="contact in contacts" :key="contact.id" :style="!contact.is_read ? 'border-left:3px solid var(--terra)' : ''">
                <td data-label="Contact">
                  <div style="display:flex;align-items:center;gap:.65rem">
                    <div class="adm-avatar" style="position:relative">
                      {{ getInitials(contact.first_name, contact.last_name) }}
                      <span v-if="!contact.is_read" style="position:absolute;top:-2px;right:-2px;width:8px;height:8px;border-radius:50%;background:var(--terra)"></span>
                    </div>
                    <div>
                      <div class="adm-cell-title" style="max-width:none">{{ contact.full_name || contact.first_name + ' ' + contact.last_name }}</div>
                      <div class="adm-cell-sub">{{ contact.email }}</div>
                    </div>
                  </div>
                </td>
                <td data-label="Téléphone">
                  <span class="adm-chip"><Phone />{{ contact.phone || '—' }}</span>
                </td>
                <td data-label="Sujet">
                  <div class="adm-cell-title">{{ contact.subject }}</div>
                  <div class="adm-cell-sub">{{ contact.message?.slice(0,60) }}…</div>
                </td>
                <td data-label="Priorité">
                  <span class="adm-badge" :class="{
                    'adm-badge--red':    contact.priority==='urgente',
                    'adm-badge--amber':  contact.priority==='haute',
                    'adm-badge--forest': contact.priority==='normale',
                    'adm-badge--gray':   contact.priority==='basse'
                  }">{{ contact.priority || 'normale' }}</span>
                </td>
                <td data-label="Statut">
                  <span class="adm-badge" :class="{
                    'adm-badge--amber':  contact.status==='nouveau',
                    'adm-badge--sage':   contact.status==='en_cours',
                    'adm-badge--green':  contact.status==='traité',
                    'adm-badge--gray':   contact.status==='archivé'
                  }">{{ contact.status || 'nouveau' }}</span>
                </td>
                <td data-label="Date" class="adm-cell-date">{{ formatDate(contact.created_at) }}</td>
                <td class="no-label">
                  <div class="adm-actions">
                    <button class="adm-btn adm-btn--outline adm-btn--xs" @click="viewContact(contact)" title="Voir"><Eye /></button>
                    <div class="adm-drop">
                      <button class="adm-btn adm-btn--outline adm-btn--xs" @click.stop="toggleActionsMenu(contact.id)"><MoreVertical /></button>
                      <div v-if="activeActionMenu===contact.id" class="adm-drop__menu">
                        <button class="adm-drop__item" @click="markAsReplied(contact)"><CheckCircle />Marquer traité</button>
                        <button class="adm-drop__item" @click="changePriority(contact)"><Flag />Priorité</button>
                        <button class="adm-drop__item adm-drop__item--red" @click="confirmDelete(contact)"><Trash2 />Supprimer</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="contactsPagination.last_page > 1" class="adm-pagination">
          <p class="adm-pagination__info">{{ contactsPagination.total }} messages</p>
          <div class="adm-pagination__pages">
            <button class="adm-page-btn" :disabled="contactsPagination.current_page===1" @click="changePage(contactsPagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="adm-page-btn" :class="{'adm-page-btn--active':contactsPagination.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="adm-page-btn" :disabled="contactsPagination.current_page===contactsPagination.last_page" @click="changePage(contactsPagination.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </div>
    </template>

    <!-- ══ MODAL : Détail contact ══════════════════════ -->
    <Transition name="adm-modal">
      <div v-if="showDetailsModal && selectedContact" class="adm-modal" @click.self="closeDetailsModal">
        <div class="adm-modal__box adm-modal__box--lg">
          <div class="adm-modal__head">
            <h3 class="adm-modal__title">Message de {{ selectedContact.full_name || selectedContact.first_name + ' ' + selectedContact.last_name }}</h3>
            <button class="adm-modal__close" @click="closeDetailsModal"><X /></button>
          </div>
          <div class="adm-modal__body">
            <!-- Info contact -->
            <div class="adm-detail-grid">
              <div class="adm-detail-row"><p class="adm-detail-row__label">Nom</p><p class="adm-detail-row__val">{{ selectedContact.full_name || selectedContact.first_name + ' ' + selectedContact.last_name }}</p></div>
              <div class="adm-detail-row"><p class="adm-detail-row__label">Email</p><p class="adm-detail-row__val">{{ selectedContact.email }}</p></div>
              <div class="adm-detail-row"><p class="adm-detail-row__label">Téléphone</p><p class="adm-detail-row__val">{{ selectedContact.phone || '—' }}</p></div>
              <div class="adm-detail-row"><p class="adm-detail-row__label">Date</p><p class="adm-detail-row__val">{{ formatDateTime(selectedContact.created_at) }}</p></div>
            </div>
            <!-- Sujet + Message -->
            <div class="adm-field">
              <label class="adm-label">Sujet</label>
              <p style="font-weight:500;color:var(--ink)">{{ selectedContact.subject }}</p>
            </div>
            <div class="adm-field">
              <label class="adm-label">Message</label>
              <div style="background:var(--cream);border-radius:6px;padding:1rem;font-size:.88rem;color:var(--ink-60);line-height:1.7;white-space:pre-wrap">{{ selectedContact.message }}</div>
            </div>
            <!-- Statut + Priorité -->
            <div class="adm-form-grid">
              <div class="adm-field">
                <label class="adm-label">Statut</label>
                <select v-model="selectedContact.status" class="adm-input" @change="updateContactStatus">
                  <option value="nouveau">Nouveau</option>
                  <option value="en_cours">En cours</option>
                  <option value="traité">Traité</option>
                  <option value="archivé">Archivé</option>
                </select>
              </div>
              <div class="adm-field">
                <label class="adm-label">Priorité</label>
                <select v-model="selectedContact.priority" class="adm-input" @change="updateContactPriority">
                  <option value="basse">Basse</option>
                  <option value="normale">Normale</option>
                  <option value="haute">Haute</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
            </div>
            <!-- Notes admin -->
            <div class="adm-field">
              <label class="adm-label">Notes internes</label>
              <textarea v-model="selectedContact.admin_notes" rows="3" class="adm-input adm-textarea" placeholder="Notes visibles uniquement par l'équipe admin…"></textarea>
            </div>
          </div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="updateContactNotes">Sauvegarder notes</button>
            <button class="adm-btn adm-btn--green" @click="markAsReplied(selectedContact)"><CheckCircle />Marquer traité</button>
            <button class="adm-btn adm-btn--forest" @click="closeDetailsModal">Fermer</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ── Priorité modal ───────────────────────────── -->
    <Transition name="adm-modal">
      <div v-if="showPriorityModal" class="adm-modal" @click.self="showPriorityModal=false">
        <div class="adm-modal__box">
          <div class="adm-modal__head">
            <h3 class="adm-modal__title">Changer la priorité</h3>
            <button class="adm-modal__close" @click="showPriorityModal=false"><X /></button>
          </div>
          <div class="adm-modal__body">
            <div v-for="p in priorities" :key="p.value" class="adm-priority-opt" :class="{'adm-priority-opt--active':selectedPriority===p.value}" @click="selectedPriority=p.value">
              <div class="adm-priority-opt__dot" :style="{background: p.value==='urgente'?'var(--red)':p.value==='haute'?'var(--amber)':p.value==='normale'?'var(--forest)':'var(--ink-60)'}"></div>
              <div>
                <p style="font-weight:500;font-size:.88rem">{{ p.label }}</p>
                <p style="font-size:.78rem;color:var(--ink-60)">{{ p.description }}</p>
              </div>
            </div>
          </div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showPriorityModal=false">Annuler</button>
            <button class="adm-btn adm-btn--terra" @click="submitPriority">Confirmer</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ── Delete confirm ───────────────────────────── -->
    <Transition name="adm-modal">
      <div v-if="showDeleteConfirm" class="adm-modal" @click.self="showDeleteConfirm=false">
        <div class="adm-modal__box">
          <div class="adm-modal__head">
            <h3 class="adm-modal__title">Supprimer le message ?</h3>
            <button class="adm-modal__close" @click="showDeleteConfirm=false"><X /></button>
          </div>
          <div class="adm-modal__body">
            <div class="adm-confirm">
              <div class="adm-confirm__icon"><Trash2 /></div>
              <h3>Confirmer la suppression</h3>
              <p>Le message de « {{ contactToDelete?.full_name || contactToDelete?.first_name }} » sera définitivement supprimé.</p>
            </div>
          </div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showDeleteConfirm=false">Annuler</button>
            <button class="adm-btn adm-btn--red" @click="deleteContactHandler"><Trash2 />Supprimer</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Toast -->
    <Transition name="adm-toast">
      <div v-if="toastMessage" class="adm-toast"><CheckCircle />{{ toastMessage }}</div>
    </Transition>

  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import {
  Download, FileSpreadsheet, FileText, Search, X,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  MoreVertical, Eye, Trash2, CheckCircle,
  Phone, Mail, Calendar, Clock, UserPlus, Flag, Inbox
} from 'lucide-vue-next'

export default {
  name: 'AdminContacts',
  components: {
    Download, FileSpreadsheet, FileText, Search, X,
    ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
    MoreVertical, Eye, Trash2, CheckCircle,
    Phone, Mail, Calendar, Clock, UserPlus, Flag, Inbox
  },
  data() {
    return {
      activeActionMenu: null, showExportMenu: false,
      showDetailsModal: false, showAssignModal: false,
      showPriorityModal: false, showDeleteConfirm: false,
      selectedContact: null, contactToAssign: null,
      contactToPrioritize: null, contactToDelete: null,
      selectedUserId: null, selectedPriority: null,
      displayedPages: [], toastMessage: '',
      filters: { search:'',status:'',priority:'',assigned_to:'',sort_by:'created_at',sort_order:'desc' },
      priorities: [
        {value:'basse',   label:'Basse',   description:'À traiter sans urgence',       color:'bg-gray-400'},
        {value:'normale', label:'Normale', description:'Priorité standard',            color:'bg-primary'},
        {value:'haute',   label:'Haute',   description:'À traiter rapidement',         color:'bg-accent'},
        {value:'urgente', label:'Urgente', description:'Réponse immédiate requise',    color:'bg-destructive'}
      ]
    }
  },
  computed: {
    ...mapState({ loading:s=>s.loading, contacts:s=>s.contacts, contactsPagination:s=>s.contactsPagination, contactStats:s=>s.contactStats, users:s=>s.users })
  },
  watch: {
    'contactsPagination.last_page':{handler(){this.updateDisplayedPages()},immediate:true},
    'contactsPagination.current_page':{handler(){this.updateDisplayedPages()}},
    toastMessage:{handler(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}}
  },
  created() { this.debouncedSearch=debounce(this.applyFilters,500); this.loadData() },
  methods: {
    ...mapActions(['fetchContacts','fetchContactStats','fetchContact','updateContact','deleteContact']),
    async loadData(){await Promise.all([this.fetchContacts(),this.fetchContactStats()])},
    async viewContact(c){try{const r=await this.fetchContact(c.id);this.selectedContact=r.data;this.showDetailsModal=true;this.activeActionMenu=null;this.loadData()}catch(e){console.error(e);this.showToast('Erreur lors du chargement')}},
    closeDetailsModal(){this.showDetailsModal=false;this.selectedContact=null},
    async updateContactStatus(){if(this.selectedContact){try{await this.updateContact({id:this.selectedContact.id,data:{status:this.selectedContact.status}});this.showToast('Statut mis à jour');this.loadData()}catch(e){console.error(e)}}},
    async updateContactPriority(){if(this.selectedContact){try{await this.updateContact({id:this.selectedContact.id,data:{priority:this.selectedContact.priority}});this.showToast('Priorité mise à jour');this.loadData()}catch(e){console.error(e)}}},
    async updateContactNotes(){if(this.selectedContact){try{await this.updateContact({id:this.selectedContact.id,data:{admin_notes:this.selectedContact.admin_notes}});this.showToast('Notes sauvegardées')}catch(e){console.error(e)}}},
    async markAsReplied(c){try{await this.updateContact({id:c.id,data:{status:'traité',replied_at:new Date().toISOString()}});this.showToast('Marqué comme traité');if(this.showDetailsModal)this.closeDetailsModal();this.loadData()}catch(e){console.error(e)}},
    changePriority(c){this.contactToPrioritize=c;this.selectedPriority=c.priority;this.showPriorityModal=true;this.activeActionMenu=null},
    async submitPriority(){if(this.contactToPrioritize){try{await this.updateContact({id:this.contactToPrioritize.id,data:{priority:this.selectedPriority}});this.showToast('Priorité mise à jour');this.showPriorityModal=false;this.contactToPrioritize=null;this.loadData()}catch(e){console.error(e)}}},
    confirmDelete(c){this.contactToDelete=c;this.showDeleteConfirm=true;this.activeActionMenu=null},
   async deleteContactHandler(){if(this.contactToDelete){ try{ await this.deleteContact(this.contactToDelete.id);this.showToast('Message supprimé');this.showDeleteConfirm=false;this.contactToDelete=null;this.loadData()}catch(e){console.error(e);this.showToast('Erreur lors de la suppression')}}},
    applyFilters(){this.fetchContacts(this.filters)},
    sortBy(field){let order=this.filters.sort_by===field?(this.filters.sort_order==='asc'?'desc':'asc'):'asc';this.filters.sort_by=field;this.filters.sort_order=order;this.applyFilters()},
    resetFilters(){this.filters={search:'',status:'',priority:'',assigned_to:'',sort_by:'created_at',sort_order:'desc'};this.applyFilters()},
    changePage(p){if(p>=1&&p<=this.contactsPagination.last_page){this.fetchContacts(this.filters)}},
    updateDisplayedPages(){const c=this.contactsPagination.current_page,l=this.contactsPagination.last_page,d=2,pages=[];for(let i=1;i<=l;i++){if(i===1||i===l||(i>=c-d&&i<=c+d))pages.push(i)}this.displayedPages=pages},
    exportToExcel(){const csv=this.generateCSV();const b=new Blob([csv],{type:'text/csv'});const u=window.URL.createObjectURL(b);const a=document.createElement('a');a.href=u;a.download=`contacts_${new Date().toISOString().split('T')[0]}.csv`;a.click();this.showExportMenu=false;this.showToast('Export téléchargé')},
    exportToPDF(){this.showToast('Export PDF généré');this.showExportMenu=false},
    generateCSV(){return[['Nom','Email','Téléphone','Sujet','Priorité','Statut','Date'],...this.contacts.map(c=>[c.full_name||c.first_name+' '+c.last_name,c.email,c.phone||'',c.subject,c.priority||'',c.status||'',this.formatDate(c.created_at)])].map(r=>r.join(',')).join('\n')},
    toggleExportMenu(){this.showExportMenu=!this.showExportMenu},
    toggleActionsMenu(id){this.activeActionMenu=this.activeActionMenu===id?null:id},
    getInitials(f,l){return(f?.charAt(0)||'')+(l?.charAt(0)||'')},
    formatDate(d){if(!d)return'—';return new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'numeric'})},
    formatDateTime(d){if(!d)return'—';return new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})},
    showToast(m){this.toastMessage=m}
  },
  mounted(){document.addEventListener('click',e=>{if(!e.target.closest('.adm-drop')){this.activeActionMenu=null;this.showExportMenu=false}})}
}
</script>

<style>@import './admin.css';</style>
<style scoped>
.adm-priority-opt { display:flex; align-items:center; gap:.75rem; padding:.85rem 1rem; border-radius:6px; border:1.5px solid var(--ink-12); cursor:pointer; transition:border-color .2s ease; margin-bottom:.5rem; }
.adm-priority-opt:hover { border-color:var(--terra); }
.adm-priority-opt--active { border-color:var(--terra); background:rgba(200,90,42,.05); }
.adm-priority-opt__dot { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
</style>
